/**
 * Vehicle creation for the person-registration flows (morador, funcionário, visitante,
 * prestador). Every one of those screens ran a byte-identical POST loop differing only in
 * `owner_type`; six of the seven copies dropped the `res.ok` check that
 * VisitorRegistrationModal has always had, so a rejected vehicle POST was swallowed and the
 * operator saw only "Morador criado com sucesso!" while the vehicle was quietly discarded.
 */

import { fetchWithAuth } from './api';
import { toHttpError } from './httpError';

export interface PendingVehicle {
  plate: string;
  model?: string;
  color?: string;
}

export type VehicleOwnerType =
  | 'resident'
  | 'employee'
  | 'condo_employee'
  | 'visitor'
  | 'service_provider';

export interface VehicleCreateFailure {
  plate: string;
  message: string;
  /** 409 — the plate already exists. The common, benign case. */
  isDuplicate: boolean;
}

/**
 * Creates the vehicles captured during registration, after the person already exists.
 *
 * Deliberately non-fatal: the person has been created and committed, so a rejected vehicle
 * POST must not fail the enclosing flow. It must not be silent either — hence the returned
 * failure list, which callers pass to describeVehicleFailures() to warn the operator.
 */
export async function createVehiclesForPerson(
  customerId: string,
  personId: string,
  ownerType: VehicleOwnerType,
  vehicles: PendingVehicle[] | null | undefined,
  options?: { photoUrl?: string },
): Promise<VehicleCreateFailure[]> {
  const failures: VehicleCreateFailure[] = [];

  for (const v of vehicles || []) {
    try {
      const res = await fetchWithAuth(`/api/v1/customers/${customerId}/vehicles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plate: v.plate,
          model: v.model || undefined,
          color: v.color || undefined,
          owner_type: ownerType,
          person_id: personId,
          ...(options?.photoUrl ? { photo_url: options.photoUrl } : {}),
        }),
      });

      if (!res.ok) {
        const err = await toHttpError(res, 'não foi possível cadastrar');
        failures.push({ plate: v.plate, message: err.message, isDuplicate: res.status === 409 });
      }
    } catch (err: any) {
      // Network/abort failure — fetchWithAuth itself rejected.
      failures.push({
        plate: v.plate,
        message: err?.message || 'não foi possível cadastrar',
        isDuplicate: false,
      });
    }
  }

  return failures;
}

/**
 * One-line operator-facing summary, or null when every vehicle was created. Duplicate plates
 * are grouped and phrased separately because "already registered" is a different message from
 * a genuine failure.
 */
export function describeVehicleFailures(failures: VehicleCreateFailure[]): string | null {
  if (failures.length === 0) return null;

  const duplicates = failures.filter(f => f.isDuplicate).map(f => f.plate);
  const others = failures.filter(f => !f.isDuplicate);

  const parts: string[] = [];
  if (duplicates.length > 0) {
    parts.push(`${duplicates.join(', ')} já cadastrada${duplicates.length > 1 ? 's' : ''}`);
  }
  for (const f of others) {
    parts.push(`${f.plate}: ${f.message}`);
  }

  const noun = failures.length > 1 ? 'Veículos não cadastrados' : 'Veículo não cadastrado';
  return `${noun} — ${parts.join('; ')}`;
}
